import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { registerClientStateReset, resetQueryState } from '@/lib/queryClient'

/**
 * Tenant selection for platform context
 */
export interface PlatformTenant {
  id: string
  name: string
  tier: string
  status: string
}

interface PlatformContextState {
  // State
  selectedTenantId: string | null // null = "All Tenants" view
  selectedTenant: PlatformTenant | null

  // Actions
  setSelectedTenant: (tenant: PlatformTenant | null) => void
  clearSelectedTenant: () => void

  // Future-proofing: When we split UIs, this store won't be needed in tenant UI
  _isPlatformContext: boolean
}

/**
 * Platform Context Store
 *
 * Used by platform admins to select which tenant context they're viewing.
 * This store persists the selected tenant across page refreshes.
 *
 * Future: When platform and tenant UIs are separated, this store will only
 * exist in the platform UI application.
 */
export const usePlatformContextStore = create<PlatformContextState>()(
  persist(
    (set, get) => ({
      // Initial state - platform admins start with "All Tenants" view
      selectedTenantId: null,
      selectedTenant: null,
      _isPlatformContext: true, // Marker for future UI separation

      // Set a specific tenant context
      setSelectedTenant: (tenant: PlatformTenant | null) => {
        // X-Target-Tenant-ID follows this value: cached data and in-flight
        // requests belong to the previous target (#301).
        if ((tenant?.id || null) !== get().selectedTenantId) resetQueryState()
        set({
          selectedTenantId: tenant?.id || null,
          selectedTenant: tenant,
        })
      },

      // Clear tenant context (return to "All Tenants" view)
      clearSelectedTenant: () => {
        if (get().selectedTenantId !== null) resetQueryState()
        set({
          selectedTenantId: null,
          selectedTenant: null,
        })
      },
    }),
    {
      name: 'vharta-platform-context',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        selectedTenantId: state.selectedTenantId,
        selectedTenant: state.selectedTenant,
      }),
    }
  )
)

// The impersonation target feeds X-Target-Tenant-ID. authStore.logout only
// removes the persisted localStorage key, not this in-memory state, so a
// different user signing in on the same page load would inherit the previous
// platform admin's target (#301). A same-user tenant switch deliberately keeps
// it: see authStore.switchTenant's note on the platform admin's cursor.
registerClientStateReset((reason) => {
  if (reason !== 'tenant') usePlatformContextStore.getState().clearSelectedTenant()
}, 'platformContext')
